import { motion } from "framer-motion";
import { Button } from "../ui/button";
import { ArrowUpRight, Clock } from "lucide-react";
import { siteFlags } from "@/config/siteFlags";
import { scrollToBook } from "@/config/siteLinks";

const companyLogos = [
  { name: "Razorpay", src: "/logos/razorpay.svg", cls: "max-h-7 sm:max-h-9" },
  { name: "Groww", text: "Groww", textCls: "text-[#00B386] text-3xl sm:text-4xl font-bold tracking-tight" },
  { name: "Meesho", src: "/logos/meesho.png", cls: "max-h-7 sm:max-h-9" },
  { name: "Darwinbox", src: "/logos/darwinbox.png", cls: "max-h-7 sm:max-h-9" },
];
const stagger = { animate: { transition: { staggerChildren: 0.08 } } };
const fadeUp = {
  initial: { y: 24, opacity: 0 },
  animate: { y: 0, opacity: 1, transition: { duration: 0.7, ease: [0.22, 1, 0.36, 1] } },
};

export const Hero = () => {
  return (
    <section id="top" data-testid="hero-section" className="relative pt-32 sm:pt-40 pb-24 sm:pb-32 overflow-hidden">
      <div className="max-w-7xl mx-auto px-6 sm:px-8 md:px-12">
        <motion.div initial="initial" animate="animate" variants={stagger} className="grid grid-cols-12 gap-y-12 gap-x-8">
          <div className="col-span-12 lg:col-span-8">
            <motion.p variants={fadeUp} className="label-eyebrow text-[#143628] mb-8">
              <span className="inline-block w-8 h-px bg-[#143628] align-middle mr-3" />
              A hand-picked placement service
            </motion.p>

            <motion.h1
              variants={fadeUp}
              className="font-serif-display text-[clamp(2.75rem,7vw,6.25rem)] leading-[0.95] tracking-tight text-[#1C1C1A]"
            >
              Top engineers <em className="italic text-[#143628]">deserve</em>
              <br />
              top opportunities.
              <br />
              <span className="text-[#4A4A48]">We make the match.</span>
            </motion.h1>

            <motion.p
              variants={fadeUp}
              className="mt-10 text-lg sm:text-xl text-[#4A4A48] max-w-2xl leading-relaxed"
            >
              JobNex is a white-glove career partner for India&rsquo;s top 1% engineering talent.
            </motion.p>

            <motion.div variants={fadeUp} className="mt-12 flex flex-col sm:flex-row items-stretch sm:items-center gap-4">
              <Button
                data-testid="hero-cta-primary"
                onClick={scrollToBook}
                className="rounded-none bg-[#143628] hover:bg-[#0D241A] text-[#F9F9F7] h-14 px-8 text-base font-medium tracking-tight group"
              >
                Book a free consultation
                <ArrowUpRight className="ml-2 h-4 w-4 group-hover:translate-x-0.5 group-hover:-translate-y-0.5 transition-transform" />
              </Button>
              <a
                href="#how-it-works"
                data-testid="hero-cta-secondary"
                className="inline-flex items-center justify-center gap-2 h-14 px-6 border border-[#1C1C1A]/20 text-[#1C1C1A] hover:bg-[#1C1C1A] hover:text-[#F9F9F7] transition-colors text-base"
              >
                See how it works
              </a>
            </motion.div>

            <motion.div
              variants={fadeUp}
              data-testid="hero-slots-note"
              className="mt-10 flex items-center gap-3 text-sm text-[#4A4A48]"
            >
              <span className="relative flex h-2.5 w-2.5">
                <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-[#D4B886] opacity-75" />
                <span className="relative inline-flex rounded-full h-2.5 w-2.5 bg-[#143628]" />
              </span>
              <Clock className="h-4 w-4 text-[#143628]" />
              <span>
                <span className="text-[#1C1C1A] font-medium">Slots are filling fast</span> - limited spots open this cohort.
              </span>
            </motion.div>
          </div>

          <motion.div
            variants={fadeUp}
            className="col-span-12 lg:col-span-4 lg:pt-12 relative"
          >
            <div className="relative aspect-[4/5] w-full overflow-hidden border border-[#1C1C1A]/10">
              <img
                src="/testimonials/aarav-hero-indian.jpg?v=4"
                alt="Prasanjay S., placed candidate"
                className="absolute inset-0 w-full h-full object-cover object-center"
                loading="eager"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-[#1C1C1A]/75 via-[#1C1C1A]/20 to-transparent" />
              <div className="absolute bottom-0 left-0 right-0 p-6 text-[#F9F9F7]">
                <span className="hidden label-eyebrow text-[#D4B886] bg-white/10 px-3 py-1.5 border border-white/20 mb-4">
                  Placed Candidate
                </span>
                <blockquote className="font-serif-display text-2xl leading-[1.25] mt-2">
                  &ldquo;The market was brutal - JobNex still landed me 8+ interviews every week.&rdquo;
                </blockquote>
                <p className="text-sm text-[#F9F9F7]/85 mt-4 pt-4 border-t border-white/15">
                  - Prasanjay S., Software Engineer
                </p>
              </div>
            </div>          </motion.div>
        </motion.div>

        {siteFlags.showCompanyLogos && (
          <div className="mt-24 sm:mt-32 pt-10 border-t border-[#1C1C1A]/10">
            <p className="label-eyebrow text-[#4A4A48] mb-8 text-center sm:text-left">
              Trusted by
            </p>            <div
              data-testid="trust-logos"
              className="grid grid-cols-2 sm:grid-cols-4 gap-4 sm:gap-5"
            >
              {companyLogos.map((c) => (
                <div
                  key={c.name}
                  data-testid={`trust-logo-${c.name.toLowerCase()}`}
                  className="bg-white border border-[#1C1C1A]/10 h-24 sm:h-28 flex items-center justify-center px-6 transition-shadow hover:shadow-[0_10px_30px_-15px_rgba(20,54,40,0.25)]"
                >
                  {c.text ? (
                    <span className={c.textCls}>{c.text}</span>
                  ) : (
                    <img
                      src={c.src}
                      alt={c.name}
                      loading="lazy"
                      className={`w-auto object-contain ${c.cls}`}
                    />
                  )}
                </div>
              ))}
            </div>
          </div>
        )}
      </div>
    </section>  );
};

export default Hero;
